import React from 'react';
import { Box, Card, CardContent, CardHeader, Switch, Typography } from '@material-ui/core';
import { TIER_DISCOUNT_TYPES } from '../../../constants/pricingTierConfig';
import {
    cartRowsToThresholds,
    getCartThresholdDisplayItems,
} from '../../../constants/cartDiscountTierConfig';

const sidebarCardStyle = (isMobile) =>
    isMobile
        ? {
              marginBottom: 12,
              borderRadius: 12,
              border: '1px solid #E8E8E8',
              boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.06)',
          }
        : { marginBottom: 16 };

const SummaryRow = ({ label, value }) => (
    <Box mb={2}>
        <Typography variant="caption" color="textSecondary" display="block" style={{ fontWeight: 600 }}>
            {label}
        </Typography>
        <Typography variant="body2" style={{ marginTop: 2 }}>
            {value || '—'}
        </Typography>
    </Box>
);

export const CartDiscountTierStatusPanel = ({ active, onActiveChange, isMobile, inline }) => {
    if (inline) {
        return (
            <Box display="flex" alignItems="center">
                <Switch
                    color="primary"
                    checked={active}
                    onChange={(e) => onActiveChange(e.target.checked)}
                    size="small"
                />
                <Typography variant="caption" style={{ color: active ? '#2E7D32' : '#999', fontSize: 12 }}>
                    {active ? 'Activa' : 'Inactiva'}
                </Typography>
            </Box>
        );
    }

    return (
        <Card elevation={isMobile ? 0 : 2} style={sidebarCardStyle(isMobile)}>
            <CardHeader
                titleTypographyProps={{ variant: isMobile ? 'subtitle1' : 'h6' }}
                title="Estado de la regla"
            />
            <CardContent style={isMobile ? { padding: '8px 16px 16px' } : { paddingTop: 0 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">Regla activa</Typography>
                    <Switch
                        color="primary"
                        checked={active}
                        onChange={(e) => onActiveChange(e.target.checked)}
                    />
                </Box>
                <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
                    {active
                        ? 'El descuento puede aplicar según el total del carrito y su vigencia.'
                        : 'La regla queda guardada pero inactiva en la tienda.'}
                </Typography>
            </CardContent>
        </Card>
    );
};

const CartDiscountTierSummaryPanel = ({ form, storeCurrency, isEdit, isMobile }) => {
    const scheduleText = form.useSchedule
        ? form.starts_at && form.ends_at
            ? `Del ${form.starts_at} al ${form.ends_at} (solo en ese periodo)`
            : 'Completa las fechas de inicio y fin'
        : isEdit
          ? 'Sin fechas: aplica mientras esté activa'
          : 'Sin fechas: aplica al guardar mientras esté activa';

    const tierLevelCount = cartRowsToThresholds(form.thresholds || []).length;
    const discountTypeLabel =
        TIER_DISCOUNT_TYPES.find((t) => t.id === form.discount_type)?.label || '—';
    const thresholdPreview = getCartThresholdDisplayItems(
        { discount_type: form.discount_type, thresholds: cartRowsToThresholds(form.thresholds || []) },
        storeCurrency
    )
        .map((item) => item.chipLabel)
        .join(' · ');

    return (
        <Card elevation={isMobile ? 0 : 2} style={sidebarCardStyle(isMobile)}>
            <CardHeader
                titleTypographyProps={{ variant: isMobile ? 'subtitle1' : 'h6' }}
                title="Resumen de la regla"
            />
            <CardContent style={isMobile ? { padding: '8px 16px 16px' } : { paddingTop: 0 }}>
                <SummaryRow label="Nombre" value={form.name?.trim() || '—'} />
                <SummaryRow label="Alcance" value="Toda la tienda" />
                <SummaryRow
                    label="Niveles"
                    value={
                        tierLevelCount > 0
                            ? `${tierLevelCount} nivel${tierLevelCount > 1 ? 'es' : ''}`
                            : '—'
                    }
                />
                <SummaryRow label="Tipo de descuento" value={discountTypeLabel} />
                <SummaryRow label="Escalones" value={thresholdPreview || '—'} />
                <SummaryRow label="Vigencia" value={scheduleText} />
                <SummaryRow label="Estado" value={form.active ? 'Activa' : 'Inactiva'} />
            </CardContent>
        </Card>
    );
};

const CartDiscountTierSidebar = (props) => (
    <Box>
        <CartDiscountTierSummaryPanel {...props} />
        {!props.isMobile && (
            <CartDiscountTierStatusPanel
                active={props.form.active}
                onActiveChange={props.onActiveChange}
                isMobile={false}
            />
        )}
    </Box>
);

export default CartDiscountTierSidebar;
